import { useCallback, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router';

import { isDay, today } from '../../dates';
import { useLanguage } from '../../contexts/language';
import { useRecord } from '../../hooks/record';
import { useVisit } from '../../hooks/visit';

import { CheckStep } from '../check-step';
import { LevelStep } from '../level-step';
import { LoadFailed } from '../load-failed';
import { MedicationStep } from '../medication-step';
import { MoodStep } from '../mood-step';
import { RecordDate } from '../record-date';
import { SleepStep } from '../sleep-step';
import { StepProgress } from '../step-progress';

import styles from './style.module.css';

// The six steps of the daily record, in order (C20). The path names the step
// so Back and the browser's history move between them.
const steps = [ 'mood', 'anxiety', 'irritability', 'sleep', 'medication', 'check' ];

// The daily record for one day: the frame, the counter, and which step is on
// screen. Each step saves its own answers when it moves on, so closing half
// way keeps everything answered so far; Skip moves on without saving, and an
// answer never comes from another day.
//
// The day is in the path and can be changed from the "Recording for" field;
// a malformed day or one that has not happened yet goes back to the Diary.
export function DailyRecord() {
  const { text } = useLanguage();
  const navigate = useNavigate();
  const { date, step } = useParams();
  const { record, medications, error, retry, reload } = useRecord(date);
  const capture = useVisit();
  const number = steps.indexOf(step) + 1;
  // The step on screen, for the bar to ask it to save before moving (#158).
  const stepRef = useRef(null);
  // The move under way, from the moment a step starts saving until it opens
  // its destination or stops: the day and step it left from, and where it
  // goes (null for the next step). The bar is held for that day and step
  // only, so leaving them never leaves it held; a newer move is never
  // released by an older one, since each is its own object.
  const [ moving, setMoving ] = useState(null);
  const busy = moving != null && moving.date === date && moving.step === step;

  const go = useCallback(function(name) {
    navigate(`/diary/record/${ date }/${ name }`);
  }, [ date, navigate ]);

  // Where a step goes without saving: the chosen step, or the next one. Skip
  // calls it with its click, which is not a step name.
  const onMove = useCallback(function(to) {
    if (typeof to === 'string') {
      return go(to);
    }

    return go(steps[ number ]);
  }, [ number, go ]);

  // A step starting to save: the bar is held until the move ends.
  const onSaving = useCallback(function(to) {
    const move = { date, step, to };

    setMoving(move);

    return move;
  }, [ date, step ]);

  // A move that ended without opening its destination: refused, or its visit
  // left. Only that very move is released.
  const onStopped = useCallback(function(move) {
    setMoving(function(current) {
      if (current === move) {
        return null;
      }

      return current;
    });
  }, []);

  // A step chosen on the bar is reached as Next reaches the next one: the
  // step on screen saves first, with the chosen step as its destination, and
  // a refusal keeps it on screen. Nothing is chosen while a move is under
  // way; Check has nothing to save, so it just goes; the current step goes
  // nowhere.
  const onStep = useCallback(function(name) {
    if (busy === true || name === step) {
      return;
    }

    if (step === 'check') {
      go(name);

      return;
    }

    stepRef.current.submit(name);
  }, [ busy, step, go ]);

  // The first step's Back closes the record, like the Close link above it.
  const onBack = useCallback(function() {
    if (number === 1) {
      return navigate(`/diary?day=${ date }`);
    }

    return go(steps[ number - 2 ]);
  }, [ number, date, go, navigate ]);

  // Saved answers are read back before the destination opens, so it starts
  // from what the service holds. A step calls this only while its visit
  // lasts; the destination opens only if the record is still on that visit
  // once the read has answered (#135). The move ends either way.
  const onDone = useCallback(function(move) {
    const owns = capture();

    return reload().then(function() {
      onStopped(move);

      if (owns() === true) {
        onMove(move.to);
      }
    });
  }, [ reload, onStopped, onMove, capture ]);

  const onSaved = useCallback(function() {
    navigate(`/diary?day=${ date }`);
  }, [ date, navigate ]);

  // The bar's steps, named in the device language; the same list until the
  // language changes, so the bar is not drawn again for nothing.
  const stepList = useMemo(function() {
    return [
      { name: 'mood', label: text.mood },
      { name: 'anxiety', label: text.anxiety },
      { name: 'irritability', label: text.irritability },
      { name: 'sleep', label: `${ text.hoursSlept } · ${ text.weight }`, icons: [ 'sleep', 'weight' ] },
      { name: 'medication', label: text.medicationQuestion },
      { name: 'check', label: text.checkTitle }
    ];
  }, [ text ]);

  if (isDay(date) === false || date > today()) {
    return <Navigate to='/diary' replace/>;
  }

  if (number === 0) {
    return <Navigate to={ `/diary/record/${ date }/mood` } replace/>;
  }

  if (error != null) {
    return <LoadFailed onRetry={ retry }/>;
  }

  if (record === undefined) {
    return null;
  }

  let screen = <MoodStep key={ date } record={ record } ref={ stepRef } onBack={ onBack } onSkip={ onMove } onMove={ onMove } onSaving={ onSaving } onStopped={ onStopped } onDone={ onDone }/>;

  if (step === 'anxiety') {
    screen = <LevelStep key={ `${ date }-anxiety` } field='anxiety' question={ text.anxietyQuestion } record={ record } ref={ stepRef } onBack={ onBack } onSkip={ onMove } onMove={ onMove } onSaving={ onSaving } onStopped={ onStopped } onDone={ onDone }/>;
  }

  if (step === 'irritability') {
    screen = <LevelStep key={ `${ date }-irritability` } field='irritability' question={ text.irritabilityQuestion } record={ record } ref={ stepRef } onBack={ onBack } onSkip={ onMove } onMove={ onMove } onSaving={ onSaving } onStopped={ onStopped } onDone={ onDone }/>;
  }

  if (step === 'sleep') {
    screen = <SleepStep key={ date } record={ record } ref={ stepRef } onBack={ onBack } onSkip={ onMove } onMove={ onMove } onSaving={ onSaving } onStopped={ onStopped } onDone={ onDone }/>;
  }

  if (step === 'medication') {
    screen = <MedicationStep key={ date } record={ record } medications={ medications } ref={ stepRef } onBack={ onBack } onSkip={ onMove } onMove={ onMove } onSaving={ onSaving } onStopped={ onStopped } onDone={ onDone } onListChanged={ reload }/>;
  }

  if (step === 'check') {
    screen = <CheckStep record={ record } onBack={ onBack } onSaved={ onSaved }/>;
  }

  return (
    <section className={ styles.record }>
      <div className={ styles.top }>
        <Link className={ styles.close } to={ `/diary?day=${ date }` }>{ text.close }</Link>

        <RecordDate date={ date } step={ step }/>
      </div>

      <div className={ styles.counter }>
        <span>{ text.recordStep.replace('{step}', number) }</span>
      </div>

      <StepProgress step={ number } steps={ stepList } label={ text.recordSteps } busy={ busy } onStep={ onStep }/>

      { screen }
    </section>
  );
}
